import { useNavigate } from '@tanstack/react-router'
import { Ban, Send, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

import type { CustomerDetail } from '@/api/types'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { Button } from '@/components/ui/button'

import { useApproveCustomerKyc, useRejectCustomerKyc, useSubmitCustomerForReview } from './mutations'
import { useCustomerPermissions } from './permissions'
import { ReasonDialog } from './reason-dialog'

type OpenDialog = 'submit' | 'approve' | 'reject' | null

/**
 * The KYC decision controls.
 *
 * What is offered follows the record's own state, not the operator's hopes for it: a draft is
 * submitted, a pending file is approved or rejected, and a settled file offers nothing at all.
 * Approval is dual control — the API refuses an approver who is the person that submitted the
 * record — so a screen that showed the button to everyone would mostly be showing a 403 in
 * waiting.
 */
export function KycActions({ customer }: { customer: CustomerDetail }) {
  const permissions = useCustomerPermissions()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<OpenDialog>(null)
  // Set when the approval came back deferred: the request id of the stored countersignature
  // request. Drives the acknowledgement dialog below — a deferred approval that merely closed
  // the dialog read as "nothing happened" to the first UAT sitting, and was reported as a defect.
  const [queuedRequestId, setQueuedRequestId] = useState<string | null>(null)

  const close = () => setDialog(null)

  const submit = useSubmitCustomerForReview(customer.id)
  const approve = useApproveCustomerKyc(customer.id)
  const reject = useRejectCustomerKyc(customer.id)

  const canSubmit = customer.kycStatus === 'Draft' && permissions.canCreate
  const canDecide = customer.kycStatus === 'PendingReview' && permissions.canApproveKyc

  if (!canSubmit && !canDecide) {
    return null
  }

  return (
    <>
      {canSubmit ? (
        <Button variant="primary" onClick={() => setDialog('submit')}>
          <Send />
          Submit for review
        </Button>
      ) : null}

      {canDecide ? (
        <>
          <Button variant="secondary" onClick={() => setDialog('reject')}>
            <Ban />
            Reject
          </Button>
          <Button variant="primary" onClick={() => setDialog('approve')}>
            <ShieldCheck />
            Approve KYC
          </Button>
        </>
      ) : null}

      <ConfirmDialog
        open={dialog === 'submit'}
        onOpenChange={(open) => {
          if (!open) {
            close()
          }
        }}
        title="Submit this file for review"
        description={`${customer.fullName} moves from draft to pending review, and can no longer be edited until a supervisor decides.`}
        confirmLabel="Submit for review"
        isPending={submit.isPending}
        error={submit.isError ? submit.error : null}
        onConfirm={() => {
          submit.mutate(undefined, { onSuccess: close })
        }}
      >
        <p className="text-sm text-body">
          A file cannot leave draft without a Ghana Card, a residence address, and account-operation
          consent. If any of the three is missing the submission is refused and nothing changes.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'approve'}
        onOpenChange={(open) => {
          if (!open) {
            close()
          }
        }}
        title="Approve this KYC file"
        description={`${customer.fullName} becomes a fully onboarded customer and can hold accounts. The approval is itself countersigned — confirming may store a request for a second manager rather than complete now, and the outcome will say which.`}
        confirmLabel="Approve"
        isPending={approve.isPending}
        error={approve.isError ? approve.error : null}
        onConfirm={() => {
          approve.mutate(undefined, {
            onSuccess: (result) => {
              close()
              if (result.awaitingApproval) {
                setQueuedRequestId(result.approvalRequestId ?? 'pending')
              }
            },
          })
        }}
      >
        <p className="text-sm text-body">
          You are recorded as the approver, under your own name. Approval is refused if you are the
          person who submitted this file — the two decisions are held by two people deliberately.
        </p>
      </ConfirmDialog>

      <ReasonDialog
        open={dialog === 'reject'}
        onOpenChange={(open) => {
          if (!open) {
            close()
          }
        }}
        title="Reject this KYC file"
        description={`${customer.fullName} returns to the branch for correction.`}
        reasonLabel="Reason for rejection"
        reasonDescription="Say what has to change. This is what the branch acts on, and what the audit trail keeps."
        confirmLabel="Reject file"
        tone="danger"
        isPending={reject.isPending}
        error={reject.isError ? reject.error : null}
        onConfirm={(reason) => {
          reject.mutate(reason, { onSuccess: close })
        }}
      />

      <ConfirmDialog
        open={queuedRequestId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setQueuedRequestId(null)
          }
        }}
        title="Sent for approval — the file is unchanged"
        description={`Your approval of ${customer.fullName} is stored and waiting for a different manager to countersign it under Approvals. Nothing changes on this record until they do, and the file still reads Pending review — that is the control working, not the approval failing.`}
        confirmLabel="Track it in Approvals"
        cancelLabel="Stay here"
        onConfirm={() => {
          setQueuedRequestId(null)
          void navigate({ to: '/approvals' })
        }}
      >
        {queuedRequestId !== 'pending' ? (
          <p className="text-xs text-muted">
            Request <span className="font-mono">{queuedRequestId}</span>. The countersigner cannot
            be you — the inbox hides what you raised.
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  )
}
